import { cellAt, hasWall } from '../board/board';
import { cellDistance, cellsWithin, gridOf, line, neighbours } from '../board/geometry';
import type { Board, Cell } from '../board/types';
import type { FactionId, GameContext, GameState, Piece } from './types';

export interface Destination {
  x: number;
  y: number;
  /** Set when moving there starts a battle. */
  attacks?: Piece;
}

/**
 * Square → piece lookups, built once per (immutable) piece list. States are plain data that is
 * never mutated, so the list identity is a safe key and the cache stays pure.
 */
const occupancy = new WeakMap<readonly Piece[], Map<number, Piece>>();

function occupancyOf(state: GameState): Map<number, Piece> {
  let index = occupancy.get(state.pieces);
  if (!index) {
    index = new Map();
    for (const p of state.pieces) index.set(p.y * 1024 + p.x, p);
    occupancy.set(state.pieces, index);
  }
  return index;
}

/**
 * Each side's pieces as the squares show them (the one piece an occupancy lookup finds there), per
 * (immutable) piece list: whom an attack could reach.
 */
const shown = new WeakMap<readonly Piece[], Map<FactionId, Piece[]>>();

function shownBySide(state: GameState): Map<FactionId, Piece[]> {
  let sides = shown.get(state.pieces);
  if (!sides) {
    sides = new Map();
    const index = occupancyOf(state);
    for (const p of state.pieces) {
      if (index.get(p.y * 1024 + p.x) !== p) continue;
      const side = sides.get(p.faction);
      if (side) side.push(p);
      else sides.set(p.faction, [p]);
    }
    shown.set(state.pieces, sides);
  }
  return sides;
}

export function pieceAt(state: GameState, x: number, y: number): Piece | undefined {
  return occupancyOf(state).get(y * 1024 + x);
}

export function pieceById(state: GameState, id: number): Piece | undefined {
  return state.pieces.find((p) => p.id === id);
}

/**
 * What the flood fills ask of a board again and again, worked out once per board (boards are
 * never mutated, so the board is a safe key): from each cell, the cells one step reaches (on the
 * board, no wall between; void cells kept, as `neighbours` gives them), and the cells within each
 * flying range. The board AI asks for every enemy's moves at every position it judges.
 */
interface Reach {
  readonly steps: readonly (readonly Cell[])[];
  readonly within: Map<number, (readonly Cell[] | undefined)[]>;
}
const reaches = new WeakMap<Board, Reach>();

function reachOf(board: Board): Reach {
  let reach = reaches.get(board);
  if (!reach) {
    reach = {
      steps: board.cells.map((from) =>
        neighbours(board, from.x, from.y).filter(
          (cell) => !hasWall(board, from.x, from.y, cell.x, cell.y),
        ),
      ),
      within: new Map(),
    };
    reaches.set(board, reach);
  }
  return reach;
}

function cellsInRange(board: Board, x: number, y: number, range: number): readonly Cell[] {
  const { within } = reachOf(board);
  let byCell = within.get(range);
  if (!byCell) within.set(range, (byCell = []));
  return (byCell[y * board.width + x] ??= cellsWithin(board, x, y, range));
}

/**
 * Cells a piece may move to this turn.
 * - Ground: up to `range` steps to adjacent cells (four on a square board, six on a hex board)
 *   through empty cells; walls and pieces block.
 * - Fly: any cell within `range` by the board's distance (diagonals count as one on squares),
 *   passing over pieces and walls.
 * - Teleport: like fly – the mage simply appears at the destination.
 * A destination holding an enemy piece is allowed and starts a battle; friends block.
 */
export function destinationsFor(ctx: GameContext, state: GameState, piece: Piece): Destination[] {
  const out: Destination[] = [];
  eachDestination(ctx, state, piece, (cell, occupant) =>
    out.push(occupant ? { x: cell.x, y: cell.y, attacks: occupant } : { x: cell.x, y: cell.y }),
  );
  return out;
}

/**
 * The destinations that start a battle, in `destinationsFor`'s order; with `on`, only the battles
 * with that side. The board AI asks this of every enemy piece at every position it judges, so the
 * pieces that could be attacked are found first: a flier needs no more, and a walker is only
 * walked when one of them is within its steps.
 */
export function attacksFor(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  on?: FactionId,
): Destination[] {
  if (piece.imprisoned || piece.frozen > 0) return [];
  const character = ctx.roster[piece.character];
  if (!character) return [];
  const { type, range } = character.movement;
  const { board } = ctx;
  const ground = type === 'ground';
  const square = gridOf(board) === 'square';
  const targets: Piece[] = [];
  // The pieces the squares show, as `destinationsFor` sees them, and never the mover's own square.
  for (const [side, pieces] of shownBySide(state)) {
    if (side === piece.faction || (on !== undefined && side !== on)) continue;
    for (const other of pieces) {
      const dx = Math.abs(other.x - piece.x);
      const dy = Math.abs(other.y - piece.y);
      if (dx === 0 && dy === 0) continue;
      const distance = !square
        ? cellDistance(board, piece, other)
        : ground
          ? dx + dy
          : Math.max(dx, dy);
      if (distance <= range && cellAt(board, other.x, other.y)?.kind === 'floor')
        targets.push(other);
    }
  }
  if (targets.length === 0) return [];
  const out: Destination[] = [];
  if (type === 'ground')
    eachDestination(ctx, state, piece, (cell, occupant) => {
      if (occupant && (on === undefined || occupant.faction === on))
        out.push({ x: cell.x, y: cell.y, attacks: occupant });
    });
  // A flier's destinations come row by row, as `cellsWithin` lists them.
  else
    for (const target of targets.sort((a, b) => a.y - b.y || a.x - b.x))
      out.push({ x: target.x, y: target.y, attacks: target });
  return out;
}

/** Hands `destinationsFor`'s cells, in its order, to `reach` with the piece standing there. */
function eachDestination(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  reach: (cell: Cell, occupant: Piece | undefined) => void,
): void {
  // Held by either hold: Imprison until the cycle favours it, Freeze for a count of its own turns.
  // It still stands on its square and still defends it, but makes no move of its own. A spell may
  // still carry a frozen piece, Teleport or Exchange, as it may not an imprisoned one: the hold is
  // on the piece's legs, not on the magic (core-rules:quality#8, kept as it plays).
  if (piece.imprisoned || piece.frozen > 0) return;
  const character = ctx.roster[piece.character];
  if (!character) return;
  const { type, range } = character.movement;
  const { board } = ctx;
  const occupants = occupancyOf(state);
  const consider = (cell: Cell) => {
    if (cell.kind !== 'floor') return false;
    const occupant = occupants.get(cell.y * 1024 + cell.x);
    if (occupant && occupant.faction === piece.faction) return false;
    reach(cell, occupant);
    return occupant === undefined;
  };

  if (type === 'ground') {
    const { steps } = reachOf(board);
    const visited = new Uint8Array(board.cells.length);
    visited[piece.y * board.width + piece.x] = 1;
    let frontier: Cell[] = [cellAt(board, piece.x, piece.y)!];
    for (let step = 0; step < range && frontier.length > 0; step++) {
      const next: Cell[] = [];
      for (const from of frontier) {
        for (const cell of steps[from.y * board.width + from.x]!) {
          const at = cell.y * board.width + cell.x;
          if (visited[at]) continue;
          visited[at] = 1;
          if (consider(cell)) next.push(cell);
        }
      }
      frontier = next;
    }
    return;
  }

  for (const cell of cellsInRange(board, piece.x, piece.y, range)) consider(cell);
}

/**
 * The cells a piece passes through on its way to a destination it may move to, excluding the
 * start and including the destination. Ground pieces follow a shortest path through empty
 * cells (the same rules as `destinationsFor`); fliers and teleporters cross straight over.
 * Purely for presentation – the rules only care about the destination.
 */
export function movementPath(
  ctx: GameContext,
  state: GameState,
  piece: Piece,
  x: number,
  y: number,
): { x: number; y: number }[] {
  const character = ctx.roster[piece.character];
  if (!character || (x === piece.x && y === piece.y)) return [];
  const straight = () => line(ctx.board, piece, { x, y }).map((c) => ({ x: c.x, y: c.y }));
  if (character.movement.type !== 'ground') return straight();

  const key = (cx: number, cy: number) => `${cx},${cy}`;
  const cameFrom = new Map<string, string>();
  const visited = new Set<string>([key(piece.x, piece.y)]);
  let frontier = [{ x: piece.x, y: piece.y }];
  while (frontier.length > 0) {
    const next: { x: number; y: number }[] = [];
    for (const from of frontier) {
      for (const cell of neighbours(ctx.board, from.x, from.y)) {
        if (cell.kind !== 'floor' || visited.has(key(cell.x, cell.y))) continue;
        if (hasWall(ctx.board, from.x, from.y, cell.x, cell.y)) continue;
        visited.add(key(cell.x, cell.y));
        cameFrom.set(key(cell.x, cell.y), key(from.x, from.y));
        if (cell.x === x && cell.y === y) {
          const path: { x: number; y: number }[] = [];
          let at = key(x, y);
          while (at !== key(piece.x, piece.y)) {
            const [px, py] = at.split(',').map(Number);
            path.push({ x: px!, y: py! });
            at = cameFrom.get(at)!;
          }
          return path.reverse();
        }
        // Occupied cells end a path (an enemy there is the destination itself, handled above).
        if (!pieceAt(state, cell.x, cell.y)) next.push({ x: cell.x, y: cell.y });
      }
    }
    frontier = next;
  }
  return straight();
}
